import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Loader2, Trophy, Skull, RotateCcw, ArrowLeft, Users, Sparkles, Flame,
  Zap, Snowflake, Shield, Scissors, Volume2, VolumeX, Crown, Check, X, ChevronRight,
  CalendarDays, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { CocoonMark } from "@/components/CocoonMark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareBar } from "@/components/ShareBar";
import { Footer } from "@/components/Footer";
import { createTensionAudio } from "@/lib/spiraleAudio";

const L = {
  fr: {
    title: "La Spirale", tagline: "Franchis les cercles, résiste aux manches, atteins le Cœur du Cocon.",
    pseudo: "Ton pseudo", start: "Entrer dans l'arène", loading: "Préparation de l'arène…",
    round: "Manche", of: "sur", survivors: "survivants", correct: "Bonne réponse !", wrong: "Mauvaise réponse",
    elimTitle: "Tu es tombé·e", rankMsg: (r) => `Tu finis ${r}ᵉ sur 100`,
    winTitle: "Le Cœur du Cocon est à toi !", winMsg: "Dernier·e survivant·e des 100. Chapeau.",
    replay: "Rejouer", home: "Accueil",
    eclat: "Éclat", combo: "Combo", pts: (n) => `+${n} Éclat`,
    modeLabel: "Choisis ton mode",
    modes: {
      normal: { name: "Normal", desc: "20 s · une erreur = éliminé" },
      hardcore: { name: "Hardcore", desc: "12 s · Éclat ×1.5 · sans pitié" },
      zen: { name: "Zen", desc: "Sans élimination · joue pour l'Éclat" },
    },
    jokers: { fifty: "50/50", freeze: "Gel", shield: "Bouclier" },
    freezeOn: "Chrono gelé", shieldOn: "Bouclier armé", shieldSaved: "Bouclier ! Tu survis.",
    fell: (n) => `${n} est tombé·e`,
    duelTitle: "Le Cœur du Cocon", duelSub: "Mort subite — le Gardien t'attend", guardian: "Le Gardien",
    lbTitle: "Le Panthéon", lbWinners: "Derniers vainqueurs", lbTop: "Meilleurs Éclats",
    lbGames: (n) => `${n} parties disputées`, lbEmpty: "Sois le·la premier·e à atteindre le Cœur.",
    recapTitle: "Ton parcours", recapYou: "Toi", recapAnswer: "Réponse", recapTimeout: "Temps écoulé",
    zenEndTitle: "Parcours Zen accompli", zenEndMsg: "Aucune élimination — seul ton Éclat compte.",
    bestCombo: "Meilleur combo", soundOn: "Couper le son", soundOff: "Activer le son",
    daily: {
      title: "Le Défi du jour", sub: "Mêmes énigmes pour tout le monde · classement remis à zéro à minuit.",
      play: "Relever le défi", done: "Défi du jour accompli", come: "Reviens demain pour un nouveau défi",
      board: "Classement du jour", players: (n) => `${n} explorateur·rice·s aujourd'hui`,
      reset: (h, m) => `Nouveau défi dans ${h} h ${m} min`,
    },
  },
  en: {
    title: "The Spiral", tagline: "Cross the circles, survive the rounds, reach the Cocoon's Core.",
    pseudo: "Your nickname", start: "Enter the arena", loading: "Preparing the arena…",
    round: "Round", of: "of", survivors: "survivors", correct: "Correct!", wrong: "Wrong answer",
    elimTitle: "You fell", rankMsg: (r) => `You finish ${r}th of 100`,
    winTitle: "The Cocoon's Core is yours!", winMsg: "Last survivor of the 100. Hats off.",
    replay: "Play again", home: "Home",
    eclat: "Radiance", combo: "Combo", pts: (n) => `+${n} Radiance`,
    modeLabel: "Choose your mode",
    modes: {
      normal: { name: "Normal", desc: "20 s · one mistake = out" },
      hardcore: { name: "Hardcore", desc: "12 s · Radiance ×1.5 · merciless" },
      zen: { name: "Zen", desc: "No elimination · play for Radiance" },
    },
    jokers: { fifty: "50/50", freeze: "Freeze", shield: "Shield" },
    freezeOn: "Timer frozen", shieldOn: "Shield armed", shieldSaved: "Shield! You survive.",
    fell: (n) => `${n} fell`,
    duelTitle: "The Cocoon's Core", duelSub: "Sudden death — the Guardian awaits", guardian: "The Guardian",
    lbTitle: "The Pantheon", lbWinners: "Latest winners", lbTop: "Top Radiance",
    lbGames: (n) => `${n} games played`, lbEmpty: "Be the first to reach the Core.",
    recapTitle: "Your run", recapYou: "You", recapAnswer: "Answer", recapTimeout: "Timed out",
    zenEndTitle: "Zen run complete", zenEndMsg: "No elimination — only your Radiance counts.",
    bestCombo: "Best combo", soundOn: "Mute", soundOff: "Unmute",
    daily: {
      title: "Daily Challenge", sub: "Same riddles for everyone · leaderboard resets at midnight.",
      play: "Take the challenge", done: "Daily challenge done", come: "Come back tomorrow for a new challenge",
      board: "Today's ranking", players: (n) => `${n} explorers today`,
      reset: (h, m) => `New challenge in ${h}h ${m}m`,
    },
  },
  es: {
    title: "La Espiral", tagline: "Cruza los círculos, resiste las rondas, alcanza el Corazón del Cocoon.",
    pseudo: "Tu apodo", start: "Entrar en la arena", loading: "Preparando la arena…",
    round: "Ronda", of: "de", survivors: "supervivientes", correct: "¡Correcto!", wrong: "Respuesta incorrecta",
    elimTitle: "Has caído", rankMsg: (r) => `Terminas ${r}º de 100`,
    winTitle: "¡El Corazón del Cocoon es tuyo!", winMsg: "Último superviviente de los 100. Enhorabuena.",
    replay: "Jugar de nuevo", home: "Inicio",
    eclat: "Fulgor", combo: "Combo", pts: (n) => `+${n} Fulgor`,
    modeLabel: "Elige tu modo",
    modes: {
      normal: { name: "Normal", desc: "20 s · un error = fuera" },
      hardcore: { name: "Hardcore", desc: "12 s · Fulgor ×1.5 · sin piedad" },
      zen: { name: "Zen", desc: "Sin eliminación · juega por el Fulgor" },
    },
    jokers: { fifty: "50/50", freeze: "Congelar", shield: "Escudo" },
    freezeOn: "Cronómetro congelado", shieldOn: "Escudo listo", shieldSaved: "¡Escudo! Sobrevives.",
    fell: (n) => `${n} cayó`,
    duelTitle: "El Corazón del Cocoon", duelSub: "Muerte súbita — el Guardián te espera", guardian: "El Guardián",
    lbTitle: "El Panteón", lbWinners: "Últimos ganadores", lbTop: "Mejores Fulgores",
    lbGames: (n) => `${n} partidas jugadas`, lbEmpty: "Sé el primero en alcanzar el Corazón.",
    recapTitle: "Tu recorrido", recapYou: "Tú", recapAnswer: "Respuesta", recapTimeout: "Tiempo agotado",
    zenEndTitle: "Recorrido Zen completado", zenEndMsg: "Sin eliminación — solo cuenta tu Fulgor.",
    bestCombo: "Mejor combo", soundOn: "Silenciar", soundOff: "Activar sonido",
    daily: {
      title: "El Reto del día", sub: "Mismos enigmas para todos · el ranking se reinicia a medianoche.",
      play: "Aceptar el reto", done: "Reto del día completado", come: "Vuelve mañana para un nuevo reto",
      board: "Ranking de hoy", players: (n) => `${n} exploradores hoy`,
      reset: (h, m) => `Nuevo reto en ${h} h ${m} min`,
    },
  },
  nl: {
    title: "De Spiraal", tagline: "Doorkruis de cirkels, overleef de rondes, bereik de Kern van de Cocon.",
    pseudo: "Je bijnaam", start: "Betreed de arena", loading: "Arena wordt klaargemaakt…",
    round: "Ronde", of: "van", survivors: "overlevenden", correct: "Juist!", wrong: "Fout antwoord",
    elimTitle: "Je viel af", rankMsg: (r) => `Je eindigt ${r}e van 100`,
    winTitle: "De Kern van de Cocon is van jou!", winMsg: "Laatste overlevende van de 100. Petje af.",
    replay: "Opnieuw spelen", home: "Home",
    eclat: "Glans", combo: "Combo", pts: (n) => `+${n} Glans`,
    modeLabel: "Kies je modus",
    modes: {
      normal: { name: "Normaal", desc: "20 s · één fout = eruit" },
      hardcore: { name: "Hardcore", desc: "12 s · Glans ×1.5 · genadeloos" },
      zen: { name: "Zen", desc: "Geen eliminatie · speel voor Glans" },
    },
    jokers: { fifty: "50/50", freeze: "Bevries", shield: "Schild" },
    freezeOn: "Timer bevroren", shieldOn: "Schild gereed", shieldSaved: "Schild! Je overleeft.",
    fell: (n) => `${n} viel af`,
    duelTitle: "De Kern van de Cocon", duelSub: "Sudden death — de Bewaker wacht", guardian: "De Bewaker",
    lbTitle: "Het Pantheon", lbWinners: "Laatste winnaars", lbTop: "Beste Glans",
    lbGames: (n) => `${n} gespeelde partijen`, lbEmpty: "Wees de eerste die de Kern bereikt.",
    recapTitle: "Jouw parcours", recapYou: "Jij", recapAnswer: "Antwoord", recapTimeout: "Tijd op",
    zenEndTitle: "Zen-parcours voltooid", zenEndMsg: "Geen eliminatie — alleen je Glans telt.",
    bestCombo: "Beste combo", soundOn: "Dempen", soundOff: "Geluid aan",
    daily: {
      title: "Dagelijkse Uitdaging", sub: "Zelfde raadsels voor iedereen · ranglijst reset om middernacht.",
      play: "Ga de uitdaging aan", done: "Dagelijkse uitdaging voltooid", come: "Kom morgen terug voor een nieuwe uitdaging",
      board: "Ranglijst van vandaag", players: (n) => `${n} verkenners vandaag`,
      reset: (h, m) => `Nieuwe uitdaging over ${h}u ${m}m`,
    },
  },
};

const DIFF = {
  facile:    { hue: 152, fr: "Facile", en: "Easy", es: "Fácil", nl: "Makkelijk" },
  moyen:     { hue: 40,  fr: "Moyen", en: "Medium", es: "Medio", nl: "Gemiddeld" },
  difficile: { hue: 20,  fr: "Difficile", en: "Hard", es: "Difícil", nl: "Moeilijk" },
  expert:    { hue: 0,   fr: "Expert", en: "Expert", es: "Experto", nl: "Expert" },
};

const RINGS = [{ r: 60, cap: 6 }, { r: 112, cap: 14 }, { r: 168, cap: 30 }, { r: 230, cap: 70 }];

const spiraleToday = () => new Date().toISOString().slice(0, 10);
const computeResetIn = () => {
  const now = new Date();
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
  const diff = Math.max(0, next - now);
  return { h: Math.floor(diff / 3600000), m: Math.floor((diff % 3600000) / 60000) };
};

const NAME_POOL = [
  "Léa", "Noah", "Mia", "Lucas", "Emma", "Hugo", "Chloé", "Adam", "Jade", "Louis",
  "Zoé", "Gabriel", "Alice", "Raphaël", "Lina", "Arthur", "Nora", "Ethan", "Rose", "Malo",
  "Aya", "Sacha", "Iris", "Tom", "Léna", "Nathan", "Anaïs", "Théo", "Camille", "Yanis",
  "Sofia", "Marius", "Elsa", "Naël", "Juliette", "Aaron", "Lou", "Isaac", "Manon", "Kylian",
  "Ambre", "Enzo", "Maya", "Milo", "Louise", "Axel", "Nina", "Timéo", "Lucie", "Rayan",
  "Clara", "Oscar", "Eva", "Léo", "Romy", "Adrien", "Salomé", "Diego", "Suzanne", "Amir",
  "Olivia", "Gaspard", "Talia", "Marin", "Selma", "Basile", "Inès", "Côme", "Alba", "Nolan",
  "Yuki", "Karim", "Freya", "Dario", "Neel", "Soan", "Léonie", "Elio", "Maëlle", "Ilyes",
  "Anouk", "Sami", "Livia", "Noé", "Kenza", "Aurèle", "Giulia", "Marco", "Ophélie", "Idris",
  "Solène", "Bilal", "Roxane", "Émile", "Naïa", "Wassim", "Elina", "Loïc", "Cléa",
];

function makeBotNames() {
  const pool = [...NAME_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const names = {};
  for (let id = 1; id <= 99; id++) names[id] = pool[(id - 1) % pool.length];
  return names;
}

function layoutAlive(aliveIds) {
  const pos = {};
  let idx = 0;
  for (let k = 0; k < RINGS.length && idx < aliveIds.length; k++) {
    const { r, cap } = RINGS[k];
    const slice = aliveIds.slice(idx, idx + cap);
    slice.forEach((id, i) => {
      const a = (i / Math.max(1, slice.length)) * 2 * Math.PI - Math.PI / 2 + k * 0.35;
      pos[id] = { x: Math.cos(a) * r, y: Math.sin(a) * r };
    });
    idx += cap;
  }
  return pos;
}

function TimerRing({ time, total, hue, frozen }) {
  const R = 24, C = 2 * Math.PI * R;
  const danger = time <= 5 && !frozen;
  const stroke = frozen ? "#7dd3fc" : danger ? "#f87171" : `hsl(${hue} 92% 60%)`;
  return (
    <div className="relative flex-shrink-0" style={{ width: 58, height: 58 }} data-testid="spirale-timer">
      <svg width="58" height="58" viewBox="0 0 58 58" className="-rotate-90">
        <circle cx="29" cy="29" r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
        <circle cx="29" cy="29" r={R} fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - Math.max(0, time) / total)}
          style={{ transition: "stroke-dashoffset 1s linear", filter: `drop-shadow(0 0 5px ${stroke})` }} />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center font-display text-lg font-semibold tabular-nums ${danger ? "text-red-400" : frozen ? "text-sky-300" : "text-foreground/90"}`}>{time}</span>
    </div>
  );
}

export default function Spirale() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = L[lang] || L.fr;

  const [phase, setPhase] = useState("intro"); // intro | loading | question | reveal | end
  const [mode, setMode] = useState("normal");
  const [pseudo, setPseudo] = useState("");
  const [session, setSession] = useState(null);
  const [round, setRound] = useState(1);
  const [finalRound, setFinalRound] = useState(8);
  const [survivors, setSurvivors] = useState(100);
  const [question, setQuestion] = useState(null);
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(20);
  const [time, setTime] = useState(20);
  const [alive, setAlive] = useState(() => Array.from({ length: 100 }, (_, i) => i));
  const [splash, setSplash] = useState(false);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [jokers, setJokers] = useState({ fifty: true, freeze: true, shield: true });
  const [removed, setRemoved] = useState([]);
  const [shieldArmed, setShieldArmed] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [feed, setFeed] = useState([]);
  const [recap, setRecap] = useState([]);
  const [lb, setLb] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [dailyLb, setDailyLb] = useState(null);
  const [dailyDone, setDailyDone] = useState(null);
  const [resetIn, setResetIn] = useState("");

  const timerRef = useRef(null);
  const timeRef = useRef(20);
  const frozenRef = useRef(false);
  const soundRef = useRef(true);
  const audioRef = useRef(null);
  const namesRef = useRef({});
  const modeRef = useRef("normal");

  const diff = question ? DIFF[question.difficulty] || DIFF.moyen : DIFF.moyen;
  const hue = diff.hue;
  const diffLabel = question ? (diff[lang] || question.difficulty) : "";
  const isFinal = round >= finalRound;
  const isExpert = question?.difficulty === "expert";
  const danger = phase === "question" && time <= 5 && !frozen;

  useEffect(() => { timeRef.current = time; }, [time]);
  useEffect(() => { frozenRef.current = frozen; }, [frozen]);
  useEffect(() => { soundRef.current = soundOn; audioRef.current?.setMuted(!soundOn); }, [soundOn]);

  const loadLb = useCallback(() => {
    api.get(`/spirale/leaderboard?lang=${lang}`).then(({ data }) => setLb(data)).catch(() => {});
  }, [lang]);
  useEffect(() => { loadLb(); }, [loadLb]);

  const loadDailyLb = useCallback(() => {
    api.get(`/spirale/daily-leaderboard?lang=${lang}`).then(({ data }) => setDailyLb(data)).catch(() => {});
  }, [lang]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => {
    loadDailyLb();
    try {
      const raw = localStorage.getItem(`coccon_spirale_daily_${spiraleToday()}`);
      setDailyDone(raw ? JSON.parse(raw) : null);
    } catch { /* noop */ }
    const tick = () => { const { h, m } = computeResetIn(); setResetIn(c.daily.reset(h, m)); };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [loadDailyLb, c.daily]);

  useEffect(() => () => { audioRef.current?.stop(); }, []);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const submit = useCallback(async (ch) => {
    if (phase !== "question" || !session) return;
    stopTimer();
    setChoice(ch);
    const tLeft = timeRef.current;
    try {
      const { data } = await api.post("/spirale/answer", { session_id: session, choice: ch, time_left: tLeft });
      setResult(data);
      setPhase("reveal");
      setScore(data.score ?? 0);
      setCombo(data.combo ?? 0);
      if (data.shielded) setShieldArmed(false);

      // Fil des éliminés + points d'arène.
      const target = data.finished && data.win ? 1 : data.survivors;
      setSurvivors(target);
      setAlive((prev) => {
        const youAlive = data.alive;
        const bots = prev.filter((id) => id !== 0);
        const keepBots = Math.max(0, target - (youAlive ? 1 : 0));
        const kept = bots.slice(0, keepBots);
        const removedIds = bots.slice(keepBots);
        if (removedIds.length) {
          const names = removedIds.slice(0, 5).map((id) => namesRef.current[id]).filter(Boolean);
          if (names.length) setFeed(names.map((n) => ({ name: n, hue })));
        }
        return youAlive ? [0, ...kept] : kept;
      });

      // Récap de la manche.
      setRecap((prev) => [...prev, {
        question: question?.question, options: question?.options || [],
        your: ch, correct: data.answer_index, points: data.points ?? 0,
        difficulty: question?.difficulty, shielded: !!data.shielded,
      }]);

      setTimeout(() => {
        if (data.finished) {
          setPhase("end");
          audioRef.current?.calm();
          if (soundRef.current) audioRef.current?.sting(!!data.win);
          loadLb();
          if (modeRef.current === "daily") {
            const rec = { score: data.score ?? 0, rank: data.rank, win: !!data.win };
            try { localStorage.setItem(`coccon_spirale_daily_${spiraleToday()}`, JSON.stringify(rec)); } catch { /* noop */ }
            setDailyDone(rec);
            loadDailyLb();
          }
          return;
        }
        setQuestion(data.next_question);
        setRound(data.round_next);
        setChoice(null);
        setResult(null);
        setRemoved([]);
        setTime(timer);
        timeRef.current = timer;
        setFrozen(false);
        setPhase("question");
      }, 2600);
    } catch {
      setPhase("question");
    }
  }, [phase, session, question, hue, timer, loadLb, loadDailyLb]);

  // Décompte.
  useEffect(() => {
    if (phase !== "question") return;
    stopTimer();
    timerRef.current = setInterval(() => {
      if (frozenRef.current) return;
      setTime((t) => {
        const nt = t - 1;
        if (nt <= 5 && nt > 0 && soundRef.current) audioRef.current?.tick();
        if (t <= 1) { stopTimer(); submit(-1); return 0; }
        timeRef.current = nt;
        return nt;
      });
    }, 1000);
    return stopTimer;
  }, [phase, round, submit]);

  // Splash cinématique + montée sonore à chaque manche.
  useEffect(() => {
    if (phase !== "question") return;
    setSplash(true);
    audioRef.current?.setRound(round);
    const id = setTimeout(() => setSplash(false), 1300);
    return () => clearTimeout(id);
  }, [phase, round]);

  const start = async (modeOverride) => {
    if (!pseudo.trim()) return;
    const m = (typeof modeOverride === "string") ? modeOverride : mode;
    setMode(m); modeRef.current = m;
    setPhase("loading");
    try {
      const { data } = await api.post("/spirale/start", { pseudo: pseudo.trim(), lang, mode: m });
      setSession(data.session_id);
      setQuestion(data.question);
      setRound(1); setSurvivors(100);
      setFinalRound(data.final_round || 8);
      setTimer(data.timer || 20); setTime(data.timer || 20); timeRef.current = data.timer || 20;
      setJokers(data.jokers || { fifty: true, freeze: true, shield: true });
      setAlive(Array.from({ length: 100 }, (_, i) => i));
      namesRef.current = makeBotNames();
      setChoice(null); setResult(null); setRemoved([]); setShieldArmed(false);
      setScore(0); setCombo(0); setFeed([]); setRecap([]); setFrozen(false);
      // Audio (déclenché par un geste utilisateur → autoplay OK).
      audioRef.current?.stop();
      audioRef.current = createTensionAudio();
      audioRef.current.setMuted(!soundRef.current);
      audioRef.current.start(1);
      setPhase("question");
    } catch {
      setPhase("intro");
    }
  };

  const replay = () => {
    audioRef.current?.stop();
    setPhase("intro"); setSession(null); setQuestion(null);
    setScore(0); setCombo(0); setFeed([]); setRecap([]);
  };

  const playJoker = async (type) => {
    if (phase !== "question" || !session || !jokers[type]) return;
    try {
      const { data } = await api.post("/spirale/joker", { session_id: session, type });
      setJokers(data.jokers || { ...jokers, [type]: false });
      if (type === "fifty" && Array.isArray(data.removed)) setRemoved(data.removed);
      if (type === "shield") setShieldArmed(true);
      if (type === "freeze") {
        setFrozen(true);
        setTimeout(() => setFrozen(false), 5000);
      }
    } catch { /* joker indisponible */ }
  };

  const pos = layoutAlive(alive);
  const inGame = phase === "question" || phase === "reveal" || phase === "end";
  const heartDur = Math.max(0.55, 1.3 - (round - 1) * 0.09);

  return (
    <div className="relative min-h-[100svh] bg-background text-foreground overflow-x-clip flex flex-col" data-testid="spirale-page">
      <div className="grain-overlay" aria-hidden />

      {/* Aurore cinématique teintée par la difficulté */}
      <div aria-hidden className="fixed inset-0 pointer-events-none z-0"
        style={{ background: `radial-gradient(120% 90% at 50% 8%, hsl(${hue} 90% 50% / 0.20), transparent 55%), radial-gradient(100% 80% at 50% 110%, hsl(${hue} 85% 45% / 0.14), transparent 60%)`, transition: "background 1.1s ease" }} />
      <motion.div aria-hidden className="fixed inset-0 pointer-events-none z-0"
        animate={{ opacity: [0.25, 0.5, 0.25] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{ background: `radial-gradient(60% 50% at 50% 42%, hsl(${hue} 95% 55% / 0.12), transparent 70%)` }} />

      {/* Vignette de danger (fin de chrono) */}
      <AnimatePresence>
        {danger && (
          <motion.div key="danger" aria-hidden initial={{ opacity: 0 }} animate={{ opacity: [0.2, 0.55, 0.2] }} exit={{ opacity: 0 }}
            transition={{ duration: 1, repeat: Infinity }} className="fixed inset-0 pointer-events-none z-20"
            style={{ background: "radial-gradient(120% 100% at 50% 50%, transparent 55%, hsl(0 85% 45% / 0.5))" }} />
        )}
      </AnimatePresence>

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3">
            {inGame && phase !== "end" && (
              <button onClick={() => setSoundOn((v) => !v)} data-testid="spirale-sound"
                aria-label={soundOn ? c.soundOn : c.soundOff}
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors">
                {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
            )}
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors inline-flex items-center gap-1.5"><ArrowLeft size={15} /> {c.home}</Link>
          </div>
        </div>
      </header>

      {/* ---------- INTRO ---------- */}
      {phase === "intro" && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
          <motion.div aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
            style={{ width: 520, height: 520, maxWidth: "90vw", maxHeight: "90vw", border: "1px solid hsl(38 90% 55% / 0.12)" }}
            animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }}>
            {RINGS.map((ring, k) => (
              <span key={k} className="absolute left-1/2 top-1/2 rounded-full"
                style={{ width: ring.r * 2, height: ring.r * 2, marginLeft: -ring.r, marginTop: -ring.r, border: "1px dashed hsl(38 90% 55% / 0.14)" }} />
            ))}
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.9 }}
            className="relative font-display text-gradient-orange text-6xl sm:text-7xl lg:text-8xl leading-none pb-[0.05em]" data-testid="spirale-title">{c.title}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.8 }}
            className="relative mt-5 text-foreground/70 text-lg sm:text-xl max-w-xl">{c.tagline}</motion.p>

          {/* Pseudo */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34 }} className="relative mt-8 w-full max-w-sm">
            <input
              data-testid="spirale-pseudo"
              value={pseudo} onChange={(e) => setPseudo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && start()}
              placeholder={c.pseudo} maxLength={24}
              className="w-full text-center bg-white/[0.04] border border-primary/35 rounded-xl px-4 py-3.5 outline-none focus:border-primary transition-colors"
            />
          </motion.div>

          {/* Défi du jour */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}
            className="relative mt-6 w-full max-w-md glass rounded-3xl border-2 border-primary/45 p-6 text-left overflow-hidden" data-testid="spirale-daily"
            style={{ boxShadow: "0 0 60px -18px hsl(28 96% 56% / 0.6)" }}>
            <div aria-hidden className="pointer-events-none absolute -top-10 -right-8 w-40 h-40 rounded-full bg-primary/20 blur-[60px]" />
            <div className="relative flex items-center gap-2 mb-1.5">
              <CalendarDays size={16} className="text-primary" />
              <h3 className="font-display text-xl text-gradient-orange">{c.daily.title}</h3>
            </div>
            <p className="relative text-sm text-foreground/65 leading-relaxed mb-3">{c.daily.sub}</p>
            <p className="relative text-[11px] uppercase tracking-[0.16em] text-primary/70 inline-flex items-center gap-1.5 mb-4"><Clock size={12} /> {resetIn}</p>

            {dailyDone ? (
              <div className="relative" data-testid="spirale-daily-done">
                <div className="flex items-center justify-between bg-primary/10 border border-primary/30 rounded-2xl px-4 py-3 mb-2.5">
                  <span className="text-sm text-foreground/80 inline-flex items-center gap-2">
                    {dailyDone.win ? <Trophy size={15} className="text-primary" /> : <Skull size={15} className="text-foreground/50" />}
                    {c.daily.done}
                  </span>
                  <span className="font-display text-lg text-primary tabular-nums inline-flex items-center gap-1.5"><Sparkles size={14} />{dailyDone.score}</span>
                </div>
                <p className="text-xs text-foreground/50 italic">{c.daily.come}</p>
              </div>
            ) : (
              <button data-testid="spirale-daily-start" onClick={() => start("daily")} disabled={!pseudo.trim()}
                className="relative btn-frost-orange w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <CalendarDays size={16} /> {c.daily.play}
              </button>
            )}

            {dailyLb && dailyLb.top?.length > 0 && (
              <div className="relative mt-5 pt-4 border-t border-white/10" data-testid="spirale-daily-lb">
                <div className="flex items-center justify-between mb-2.5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">{c.daily.board}</p>
                  <span className="text-[11px] text-foreground/40">{c.daily.players(dailyLb.players || 0)}</span>
                </div>
                <ol className="space-y-1.5">
                  {dailyLb.top.slice(0, 5).map((t, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm" data-testid={`spirale-daily-rank-${i}`}>
                      <span className={`w-5 text-center font-display ${i === 0 ? "text-primary" : "text-foreground/40"}`}>{i + 1}</span>
                      <span className="flex-1 truncate text-foreground/80 inline-flex items-center gap-1.5">{t.win && <Trophy size={12} className="text-primary flex-shrink-0" />}{t.pseudo}</span>
                      <span className="font-display tabular-nums text-primary/90">{t.score}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </motion.div>

          {/* Jeu libre : sélecteur de mode + départ */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="relative mt-8 w-full max-w-md">
            <p className="text-[11px] uppercase tracking-[0.22em] text-foreground/45 mb-3">{c.modeLabel}</p>
            <div className="grid grid-cols-3 gap-2.5" data-testid="spirale-modes">
              {["normal", "hardcore", "zen"].map((m) => {
                const on = mode === m;
                const MIcon = m === "hardcore" ? Flame : m === "zen" ? Sparkles : Trophy;
                return (
                  <button key={m} onClick={() => setMode(m)} data-testid={`spirale-mode-${m}`} aria-pressed={on}
                    className={`rounded-2xl border px-2.5 py-3.5 text-center transition-all ${on ? "border-primary bg-primary/12 shadow-[0_0_22px_-6px_hsl(28_96%_56%/0.6)]" : "border-white/10 hover:border-primary/35 bg-white/[0.02]"}`}>
                    <MIcon size={18} className={`mx-auto mb-1.5 ${on ? "text-primary" : "text-foreground/50"}`} />
                    <span className={`block text-sm font-semibold ${on ? "text-primary" : "text-foreground/75"}`}>{c.modes[m].name}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-foreground/50 mt-2.5 min-h-[1.5em]">{c.modes[mode].desc}</p>
            <button data-testid="spirale-start" onClick={() => start()} disabled={!pseudo.trim()}
              className="btn-frost-orange mt-4 w-full py-4 rounded-full font-semibold text-base disabled:opacity-50">{c.start}</button>
          </motion.div>

          {/* Panthéon */}
          {lb && (lb.winners?.length > 0 || lb.top_scores?.length > 0 || lb.games_played > 0) && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.62 }}
              className="relative mt-10 w-full max-w-md glass rounded-3xl border border-primary/20 p-6 text-left" data-testid="spirale-leaderboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg text-gradient-orange inline-flex items-center gap-2"><Crown size={16} className="text-primary" /> {c.lbTitle}</h3>
                <span className="text-[11px] text-foreground/45">{c.lbGames(lb.games_played || 0)}</span>
              </div>
              {lb.top_scores?.length > 0 ? (
                <ol className="space-y-1.5">
                  {lb.top_scores.slice(0, 5).map((t, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm" data-testid={`spirale-lb-top-${i}`}>
                      <span className={`w-5 text-center font-display ${i === 0 ? "text-primary" : "text-foreground/40"}`}>{i + 1}</span>
                      <span className="flex-1 truncate text-foreground/80 inline-flex items-center gap-1.5">
                        {t.win && <Trophy size={12} className="text-primary flex-shrink-0" />}{t.pseudo}
                      </span>
                      <span className="font-display tabular-nums text-primary/90">{t.score}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-foreground/50 italic">{c.lbEmpty}</p>
              )}
            </motion.div>
          )}
        </main>
      )}

      {phase === "loading" && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center gap-5 text-foreground/60 px-6" data-testid="spirale-loading">
          <div className="relative" style={{ width: 90, height: 90 }}>
            <motion.span className="absolute inset-0 rounded-full border border-primary/25" animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} />
            <Loader2 className="absolute inset-0 m-auto animate-spin text-primary" size={38} />
          </div>
          <p className="font-display italic text-xl">{c.loading}</p>
        </main>
      )}

      {/* ---------- EN JEU ---------- */}
      {inGame && (
        <main className="relative z-10 flex-1 flex flex-col items-center px-4 sm:px-6 pt-20 pb-14 w-full">
          {/* HUD */}
          {phase !== "end" && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-3xl flex items-center justify-between gap-3 sm:gap-4 glass rounded-2xl border border-white/10 px-4 sm:px-5 py-3 mb-4" data-testid="spirale-status">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.14em] text-foreground/45 hidden sm:inline">{c.round}</span>
                <span className="font-display text-2xl text-gradient-orange leading-none">{round}</span>
                <span className="text-foreground/40 text-sm">/ {finalRound}</span>
              </div>
              <div className="flex items-center gap-1.5 text-primary" data-testid="spirale-score">
                <Sparkles size={15} />
                <span className="font-display text-xl tabular-nums leading-none">{score}</span>
                <AnimatePresence>
                  {combo >= 2 && (
                    <motion.span key={combo} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                      className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-primary/20 text-primary" data-testid="spirale-combo">×{combo}</motion.span>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex items-center gap-1.5 text-foreground/70">
                <Users size={15} className="text-primary/70" />
                <span className="font-display text-xl tabular-nums leading-none" data-testid="spirale-survivors">{survivors}</span>
              </div>
              {phase === "question"
                ? <TimerRing time={time} total={timer} hue={hue} frozen={frozen} />
                : <div style={{ width: 58, height: 58 }} className="flex-shrink-0" />}
            </motion.div>
          )}

          {/* Fil des éliminations */}
          <AnimatePresence>
            {phase === "reveal" && feed.length > 0 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                className="hidden lg:flex flex-col gap-1.5 fixed right-6 top-28 z-30 w-52" data-testid="spirale-feed">
                {feed.map((f, i) => (
                  <motion.div key={`${f.name}-${i}`} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                    className="text-xs text-foreground/55 bg-black/40 border border-red-400/15 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
                    <Skull size={11} className="text-red-400/70 flex-shrink-0" /> {c.fell(f.name)}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Arène */}
          {phase !== "end" && (
            <motion.div className="relative mx-auto" style={{ width: 520, height: 520, maxWidth: "94vw", maxHeight: "94vw" }} data-testid="spirale-arena"
              animate={isExpert && danger ? { x: [0, -3, 3, -2, 2, 0] } : { x: 0 }} transition={{ duration: 0.4, repeat: danger ? Infinity : 0 }}>
              <motion.svg className="absolute inset-0 w-full h-full" viewBox="-260 -260 520 520" aria-hidden
                animate={{ rotate: 360 }} transition={{ duration: 90, repeat: Infinity, ease: "linear" }}>
                {RINGS.map((ring, k) => (
                  <circle key={k} cx="0" cy="0" r={ring.r} fill="none" stroke={`hsl(${hue} 90% 56% / 0.18)`} strokeWidth="1" strokeDasharray="2 9" style={{ transition: "stroke 1s ease" }} />
                ))}
                <circle cx="0" cy="0" r="34" fill="none" stroke={`hsl(${hue} 95% 62% / 0.5)`} strokeWidth="1.5" style={{ transition: "stroke 1s ease" }} />
              </motion.svg>
              {/* halo du cœur — bat plus vite en fin de partie */}
              <motion.div className="absolute left-1/2 top-1/2 rounded-full pointer-events-none"
                style={{ width: 84, height: 84, marginLeft: -42, marginTop: -42, background: `radial-gradient(circle, hsl(${hue} 95% 62% / 0.5), transparent 70%)`, transition: "background 1s ease" }}
                animate={{ scale: [1, 1.22, 1], opacity: [0.5, 0.95, 0.5] }} transition={{ duration: heartDur, repeat: Infinity }} />
              {alive.map((id) => {
                const p = pos[id];
                if (!p) return null;
                const you = id === 0;
                return (
                  <motion.div key={id} className="absolute rounded-full"
                    initial={false}
                    animate={{ x: p.x, y: p.y, opacity: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ type: "spring", stiffness: 120, damping: 18 }}
                    style={{
                      left: "50%", top: "50%",
                      width: you ? 18 : 9, height: you ? 18 : 9, marginLeft: you ? -9 : -4.5, marginTop: you ? -9 : -4.5,
                      background: you ? `hsl(${hue} 95% 62%)` : "hsl(28 55% 55% / 0.7)",
                      boxShadow: you ? `0 0 16px hsl(${hue} 95% 62% / 0.95)` : "0 0 5px hsl(28 80% 55% / 0.4)",
                      border: you ? "2px solid #fff6e6" : "none", zIndex: you ? 20 : 10,
                      transition: "background 0.8s ease, box-shadow 0.8s ease",
                    }}
                    data-testid={you ? "spirale-you-dot" : undefined} />
                );
              })}
            </motion.div>
          )}

          {/* Carte de question */}
          <AnimatePresence mode="wait">
            {(phase === "question" || phase === "reveal") && question && (
              <motion.div key={round + phase}
                initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-30 -mt-6 sm:-mt-10 w-full max-w-3xl glass rounded-[26px] border p-6 sm:p-9"
                style={{ borderColor: isFinal ? "hsl(45 95% 60% / 0.6)" : `hsl(${hue} 90% 55% / 0.3)`, boxShadow: `0 24px 70px hsl(${isFinal ? 45 : hue} 90% 45% / ${isFinal ? 0.3 : 0.18})`, transition: "border-color 0.8s ease, box-shadow 0.8s ease" }}
                data-testid="spirale-question-card">

                {isFinal && (
                  <div className="text-center mb-4" data-testid="spirale-duel-banner">
                    <p className="font-display text-2xl text-gradient-orange inline-flex items-center gap-2"><Crown size={20} className="text-primary" /> {c.duelTitle}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-red-300/80 mt-1">{c.duelSub}</p>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2.5 mb-5">
                  {question.category && <span className="text-[11px] uppercase tracking-[0.2em] text-foreground/55">{question.category}</span>}
                  <span className="text-[11px] uppercase tracking-[0.16em] font-semibold px-2.5 py-1 rounded-full"
                    style={{ color: `hsl(${hue} 90% 70%)`, background: `hsl(${hue} 90% 55% / 0.14)`, border: `1px solid hsl(${hue} 90% 55% / 0.4)` }}>{diffLabel}</span>
                  {shieldArmed && (
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold px-2.5 py-1 rounded-full bg-sky-400/15 text-sky-300 border border-sky-400/40 inline-flex items-center gap-1" data-testid="spirale-shield-on">
                      <Shield size={11} /> {c.shieldOn}
                    </span>
                  )}
                  {frozen && (
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold px-2.5 py-1 rounded-full bg-sky-400/15 text-sky-300 border border-sky-400/40 inline-flex items-center gap-1">
                      <Snowflake size={11} /> {c.freezeOn}
                    </span>
                  )}
                </div>
                <h2 className="font-display text-2xl sm:text-4xl leading-snug mb-7 text-center" data-testid="spirale-question">{question.question}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {question.options.map((opt, i) => {
                    const revealed = phase === "reveal" && result;
                    const isCorrect = revealed && i === result.answer_index;
                    const isYourWrong = revealed && i === choice && choice !== result.answer_index;
                    const hidden = removed.includes(i);
                    return (
                      <motion.button key={i}
                        initial={{ opacity: 0, y: 14 }} animate={{ opacity: hidden ? 0.25 : 1, y: 0 }} transition={{ delay: 0.12 + i * 0.07 }}
                        disabled={phase !== "question" || hidden} onClick={() => submit(i)}
                        whileHover={phase === "question" && !hidden ? { scale: 1.02 } : undefined} whileTap={phase === "question" && !hidden ? { scale: 0.98 } : undefined}
                        data-testid={`spirale-option-${i}`}
                        className={`text-left min-h-[3.75rem] px-4 py-3 rounded-2xl border transition-colors flex items-center gap-3 ${
                          hidden ? "border-white/5 bg-white/[0.01] line-through opacity-40"
                            : isCorrect ? "border-emerald-400 bg-emerald-400/15 text-emerald-100"
                            : isYourWrong ? "border-red-400 bg-red-400/15 text-red-100"
                            : "border-primary/30 bg-white/[0.035] hover:border-primary hover:bg-primary/[0.08]"}`}>
                        <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold border border-primary/40 text-primary/85 flex-shrink-0">{String.fromCharCode(65 + i)}</span>
                        <span className="text-sm sm:text-base leading-snug">{opt}</span>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Jokers */}
                {phase === "question" && (
                  <div className="mt-5 flex items-center justify-center gap-2.5" data-testid="spirale-jokers">
                    {[
                      { key: "fifty", Icon: Scissors },
                      { key: "freeze", Icon: Snowflake },
                      { key: "shield", Icon: Shield },
                    ].map(({ key, Icon }) => (
                      <button key={key} onClick={() => playJoker(key)} disabled={!jokers[key]} data-testid={`spirale-joker-${key}`}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-medium transition-colors ${jokers[key] ? "border-primary/40 text-primary hover:bg-primary/10" : "border-white/8 text-foreground/30 line-through"}`}>
                        <Icon size={14} /> {c.jokers[key]}
                      </button>
                    ))}
                  </div>
                )}

                {phase === "reveal" && result && (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mt-6 text-center" data-testid="spirale-reveal">
                    <p className={`font-display text-xl sm:text-2xl ${result.correct ? "text-emerald-300" : result.shielded ? "text-sky-300" : "text-red-300"}`}>
                      {result.shielded ? c.shieldSaved : result.correct ? c.correct : c.wrong}
                    </p>
                    {result.points > 0 && <p className="text-primary font-display text-lg mt-1">{c.pts(result.points)}</p>}
                    <p className="text-foreground/45 text-sm mt-1">−{result.eliminated_this_round} {c.survivors}</p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* End screen */}
          {phase === "end" && result && (
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", damping: 18 }}
              className="relative z-30 mt-10 w-full max-w-lg text-center glass rounded-[28px] border border-primary/25 p-8 sm:p-10" data-testid="spirale-end"
              style={{ boxShadow: `0 24px 80px hsl(${result.win ? 38 : 0} 90% 45% / 0.22)` }}>
              {result.win ? (
                <>
                  <motion.div animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.12, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
                    <Trophy className="mx-auto text-primary mb-5" size={56} />
                  </motion.div>
                  <h2 className="font-display text-3xl sm:text-4xl text-gradient-orange" data-testid="spirale-win">{mode === "zen" ? c.zenEndTitle : c.winTitle}</h2>
                  <p className="text-foreground/65 mt-3">{mode === "zen" ? c.zenEndMsg : c.winMsg}</p>
                </>
              ) : (
                <>
                  <Skull className="mx-auto text-foreground/50 mb-5" size={48} />
                  <h2 className="font-display text-3xl sm:text-4xl text-foreground/90" data-testid="spirale-lose">{c.elimTitle}</h2>
                  <p className="text-primary text-xl mt-3 font-semibold">{c.rankMsg(result.rank)}</p>
                </>
              )}

              {/* Score + meilleur combo */}
              <div className="mt-6 flex items-center justify-center gap-6">
                <div>
                  <p className="font-display text-4xl text-primary inline-flex items-center gap-1.5" data-testid="spirale-final-score"><Sparkles size={22} /> {score}</p>
                  <p className="text-xs uppercase tracking-wider text-foreground/45 mt-1">{c.eclat}</p>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div>
                  <p className="font-display text-4xl text-foreground/85">{result.best_combo ?? 0}</p>
                  <p className="text-xs uppercase tracking-wider text-foreground/45 mt-1">{c.bestCombo}</p>
                </div>
              </div>

              {/* Récap */}
              {recap.length > 0 && (
                <div className="mt-7 text-left" data-testid="spirale-recap">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-foreground/45 mb-3 text-center">{c.recapTitle}</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto scroll-thin pr-1">
                    {recap.map((r, i) => {
                      const ok = r.your === r.correct;
                      const d = DIFF[r.difficulty] || DIFF.moyen;
                      return (
                        <div key={i} className="flex items-start gap-3 bg-white/[0.03] border border-white/8 rounded-xl px-3 py-2.5" data-testid={`spirale-recap-${i}`}>
                          <span className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: `hsl(${d.hue} 80% 50% / 0.15)`, color: `hsl(${d.hue} 85% 68%)` }}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground/80 leading-snug">{r.question}</p>
                            <p className="text-xs mt-1 inline-flex items-center gap-1.5">
                              {ok ? <Check size={12} className="text-emerald-400" /> : <X size={12} className="text-red-400" />}
                              <span className={ok ? "text-emerald-300/80" : "text-red-300/80"}>
                                {r.your < 0 ? c.recapTimeout : `${c.recapYou}: ${r.options[r.your] ?? "—"}`}
                              </span>
                              {!ok && <span className="text-foreground/45 inline-flex items-center gap-1"><ChevronRight size={11} /> {r.options[r.correct]}</span>}
                            </p>
                          </div>
                          {r.points > 0 && <span className="text-primary font-display text-sm tabular-nums flex-shrink-0">+{r.points}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <ShareBar
                className="mt-7 pt-6 border-t border-white/10"
                lang={lang}
                testPrefix="spirale-share"
                filename="cocoon-spirale.png"
                title={c.title}
                subject={`${c.title} — ${score} ${c.eclat}`}
                summary={`${result.win ? c.winTitle : c.rankMsg(result.rank)} — ${score} ${c.eclat} · ${c.title}\n${window.location.origin}/spirale`}
                payload={{
                  lang,
                  eyebrow: c.title,
                  title_label: result.win ? c.winTitle : c.elimTitle,
                  topic: `${score} ${c.eclat}`,
                  intro: result.win ? c.winMsg : c.rankMsg(result.rank),
                  counts: `${c.bestCombo}: ${result.best_combo ?? 0}`,
                }}
              />

              <button onClick={replay} data-testid="spirale-replay" className="btn-frost-orange mt-8 w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2">
                <RotateCcw size={16} /> {c.replay}
              </button>
              <Link to="/" className="block mt-3 text-sm text-foreground/50 hover:text-primary transition-colors">{c.home}</Link>
            </motion.div>
          )}
        </main>
      )}

      {/* Splash de manche */}
      <AnimatePresence>
        {splash && phase === "question" && question && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none" data-testid="spirale-splash">
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 1.3, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="text-center">
              <p className="text-sm uppercase tracking-[0.35em]" style={{ color: `hsl(${hue} 90% 68%)` }}>
                {isFinal ? c.duelTitle : `${c.round} ${round} / ${finalRound}`}
              </p>
              <p className="font-display text-6xl sm:text-8xl leading-none pb-[0.05em]" style={{ color: `hsl(${hue} 92% 62%)`, textShadow: `0 0 40px hsl(${hue} 90% 55% / 0.6)` }}>
                {isFinal ? c.guardian : diffLabel}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flash bonne / mauvaise réponse */}
      <AnimatePresence>
        {phase === "reveal" && result && (
          <motion.div key="flash" initial={{ opacity: 0.5 }} animate={{ opacity: 0 }} transition={{ duration: 0.7 }}
            className="fixed inset-0 z-30 pointer-events-none"
            style={{ background: result.correct ? "radial-gradient(circle at 50% 45%, hsl(150 80% 45% / 0.35), transparent 60%)" : result.shielded ? "radial-gradient(circle at 50% 45%, hsl(200 85% 50% / 0.35), transparent 60%)" : "radial-gradient(circle at 50% 45%, hsl(0 85% 50% / 0.35), transparent 60%)" }} />
        )}
      </AnimatePresence>

      {phase === "intro" && <Footer />}
    </div>
  );
}
